import type { TravelerProfile } from "@shared/schema";

export interface ArchetypeDefinition {
  id: string;
  name: string;
  description: string;
  icon: string;
  profile: TravelerProfile;
}

export const TRAVELER_ARCHETYPES: ArchetypeDefinition[] = [
  {
    id: "luxury_couple",
    name: "Luxury Couple",
    description: "Intimate getaways with fine dining and upscale stays",
    icon: "heart",
    profile: {
      archetype: "luxury_couple",
      identity: { groupSize: 2 },
      mobility: {
        fitnessLevel: "moderate",
        maxWalkingDistanceKm: 5,
        mobilityConstraints: [],
        preferredDailyPace: "slow",
        earlyMorningStart: false,
        lateNightTolerance: false,
      },
      transport: {
        preferredModes: ["flight", "private_transfer", "taxi"],
        avoidModes: ["overnight_bus"],
        maxTransitHoursPerDay: 4,
        drivingComfort: "city_only",
      },
      comfort: {
        budgetLevel: "luxury",
        hotelClass: 5,
        bedTypeRequirements: ["king"],
        foodPreferences: ["fine_dining", "wine_tasting", "local_cuisine"],
        dietaryRestrictions: [],
        mealBudgetPerPerson: 100,
      },
      interests: {
        categories: ["architecture", "food", "wine", "spa", "sunset_views", "art", "shopping"],
        avoid: ["hostels", "crowded_tourist_traps", "extreme_sports"],
      },
      safety: {
        riskTolerance: "low",
        avoidNightTravel: true,
        languageComfort: [],
        specialNeeds: [],
        travelDocuments: [],
      },
    },
  },
  {
    id: "budget_solo",
    name: "Budget Solo Backpacker",
    description: "Maximum experiences on a minimal budget",
    icon: "backpack",
    profile: {
      archetype: "budget_solo",
      identity: { groupSize: 1 },
      mobility: {
        fitnessLevel: "high",
        maxWalkingDistanceKm: 15,
        mobilityConstraints: [],
        preferredDailyPace: "aggressive",
        earlyMorningStart: true,
        lateNightTolerance: true,
      },
      transport: {
        preferredModes: ["bus", "train", "walking", "local_transit"],
        avoidModes: ["private_transfer", "taxi"],
        maxTransitHoursPerDay: 10,
        drivingComfort: "any",
      },
      comfort: {
        budgetLevel: "budget",
        hotelClass: 1,
        bedTypeRequirements: [],
        foodPreferences: ["street_food", "local_markets", "self_catering"],
        dietaryRestrictions: [],
        mealBudgetPerPerson: 15,
      },
      interests: {
        categories: ["hiking", "street_food", "nightlife", "local_culture", "nature", "markets", "photography"],
        avoid: ["luxury_resorts", "expensive_restaurants", "guided_group_tours"],
      },
      safety: {
        riskTolerance: "high",
        avoidNightTravel: false,
        languageComfort: [],
        specialNeeds: [],
        travelDocuments: [],
      },
    },
  },
  {
    id: "family_with_kids",
    name: "Family with Kids",
    description: "Kid-friendly adventures the whole family will love",
    icon: "users",
    profile: {
      archetype: "family_with_kids",
      identity: { groupSize: 4 },
      mobility: {
        fitnessLevel: "moderate",
        maxWalkingDistanceKm: 5,
        mobilityConstraints: ["stroller_friendly"],
        preferredDailyPace: "slow",
        earlyMorningStart: true,
        lateNightTolerance: false,
      },
      transport: {
        preferredModes: ["flight", "car_rental", "train"],
        avoidModes: ["overnight_bus", "motorcycle"],
        maxTransitHoursPerDay: 4,
        drivingComfort: "highway",
      },
      comfort: {
        budgetLevel: "moderate",
        hotelClass: 3,
        bedTypeRequirements: ["family_room", "twin"],
        foodPreferences: ["family_restaurants", "familiar_cuisine", "local_specialties"],
        dietaryRestrictions: [],
        mealBudgetPerPerson: 30,
      },
      interests: {
        categories: ["theme_parks", "beaches", "wildlife", "museums", "playgrounds", "interactive_experiences"],
        avoid: ["extreme_sports", "nightlife", "long_hikes", "remote_areas"],
      },
      safety: {
        riskTolerance: "low",
        avoidNightTravel: true,
        languageComfort: [],
        specialNeeds: ["child_safety"],
        travelDocuments: [],
      },
    },
  },
  {
    id: "adventure_group",
    name: "Adventure Seekers",
    description: "Adrenaline-packed trips for thrill-loving crews",
    icon: "mountain",
    profile: {
      archetype: "adventure_group",
      identity: { groupSize: 6 },
      mobility: {
        fitnessLevel: "very_high",
        maxWalkingDistanceKm: 20,
        mobilityConstraints: [],
        preferredDailyPace: "aggressive",
        earlyMorningStart: true,
        lateNightTolerance: true,
      },
      transport: {
        preferredModes: ["jeep", "train", "walking", "boat"],
        avoidModes: [],
        maxTransitHoursPerDay: 8,
        drivingComfort: "any",
      },
      comfort: {
        budgetLevel: "moderate",
        hotelClass: 2,
        bedTypeRequirements: [],
        foodPreferences: ["local_cuisine", "campfire_cooking", "energy_food"],
        dietaryRestrictions: [],
        mealBudgetPerPerson: 25,
      },
      interests: {
        categories: ["hiking", "rock_climbing", "kayaking", "wildlife", "camping", "scuba_diving", "zip_lining"],
        avoid: ["shopping", "luxury_spas", "city_tours"],
      },
      safety: {
        riskTolerance: "high",
        avoidNightTravel: false,
        languageComfort: [],
        specialNeeds: [],
        travelDocuments: [],
      },
    },
  },
  {
    id: "retiree_slow",
    name: "Retiree Slow Traveler",
    description: "Relaxed pace with comfort and cultural depth",
    icon: "coffee",
    profile: {
      archetype: "retiree_slow",
      identity: { groupSize: 2 },
      mobility: {
        fitnessLevel: "low",
        maxWalkingDistanceKm: 3,
        mobilityConstraints: ["elevator_access", "minimal_stairs"],
        preferredDailyPace: "slow",
        earlyMorningStart: true,
        lateNightTolerance: false,
      },
      transport: {
        preferredModes: ["cruise", "train", "taxi", "flight"],
        avoidModes: ["overnight_bus", "motorcycle", "hiking_trail"],
        maxTransitHoursPerDay: 3,
        drivingComfort: "city_only",
      },
      comfort: {
        budgetLevel: "premium",
        hotelClass: 4,
        bedTypeRequirements: ["king", "ground_floor"],
        foodPreferences: ["fine_dining", "local_cuisine", "breakfast_included"],
        dietaryRestrictions: [],
        mealBudgetPerPerson: 60,
      },
      interests: {
        categories: ["history", "architecture", "gardens", "wine", "art", "music", "scenic_views"],
        avoid: ["extreme_sports", "nightlife", "crowded_festivals", "long_hikes"],
      },
      safety: {
        riskTolerance: "low",
        avoidNightTravel: true,
        languageComfort: [],
        specialNeeds: ["medical_access_nearby"],
        travelDocuments: [],
      },
    },
  },
  {
    id: "accessible_traveler",
    name: "Accessible Traveler",
    description: "Wheelchair-friendly routes and inclusive experiences",
    icon: "accessibility",
    profile: {
      archetype: "accessible_traveler",
      identity: { groupSize: 2 },
      mobility: {
        fitnessLevel: "low",
        maxWalkingDistanceKm: 1,
        mobilityConstraints: ["wheelchair_accessible", "elevator_access", "ramp_access", "accessible_bathroom"],
        preferredDailyPace: "slow",
        earlyMorningStart: false,
        lateNightTolerance: false,
      },
      transport: {
        preferredModes: ["accessible_van", "flight", "train"],
        avoidModes: ["bus", "motorcycle", "boat"],
        maxTransitHoursPerDay: 3,
        drivingComfort: "none",
      },
      comfort: {
        budgetLevel: "moderate",
        hotelClass: 3,
        bedTypeRequirements: ["accessible_room"],
        foodPreferences: ["accessible_restaurants", "room_service"],
        dietaryRestrictions: [],
        mealBudgetPerPerson: 40,
      },
      interests: {
        categories: ["museums", "architecture", "food", "scenic_drives", "cultural_shows", "markets"],
        avoid: ["hiking", "rock_climbing", "extreme_sports", "cobblestone_streets"],
      },
      safety: {
        riskTolerance: "low",
        avoidNightTravel: true,
        languageComfort: [],
        specialNeeds: ["wheelchair", "medical_access_nearby", "accessible_transport"],
        travelDocuments: [],
      },
    },
  },
  {
    id: "foodie_nomad",
    name: "Foodie Nomad",
    description: "Eat your way around the world, one dish at a time",
    icon: "utensils",
    profile: {
      archetype: "foodie_nomad",
      identity: { groupSize: 4 },
      mobility: {
        fitnessLevel: "moderate",
        maxWalkingDistanceKm: 8,
        mobilityConstraints: [],
        preferredDailyPace: "moderate",
        earlyMorningStart: false,
        lateNightTolerance: true,
      },
      transport: {
        preferredModes: ["walking", "taxi", "train", "flight"],
        avoidModes: [],
        maxTransitHoursPerDay: 5,
        drivingComfort: "city_only",
      },
      comfort: {
        budgetLevel: "moderate",
        hotelClass: 3,
        bedTypeRequirements: [],
        foodPreferences: ["street_food", "fine_dining", "cooking_classes", "food_markets", "wine_tasting", "local_specialties"],
        dietaryRestrictions: [],
        mealBudgetPerPerson: 60,
      },
      interests: {
        categories: ["food", "markets", "cooking_classes", "wine", "coffee_culture", "local_cuisine", "food_tours"],
        avoid: ["extreme_sports", "long_hikes"],
      },
      safety: {
        riskTolerance: "moderate",
        avoidNightTravel: false,
        languageComfort: [],
        specialNeeds: [],
        travelDocuments: [],
      },
    },
  },
  {
    id: "cultural_deep_diver",
    name: "Cultural Deep-Diver",
    description: "Immerse in history, art, and authentic local life",
    icon: "landmark",
    profile: {
      archetype: "cultural_deep_diver",
      identity: { groupSize: 2 },
      mobility: {
        fitnessLevel: "moderate",
        maxWalkingDistanceKm: 10,
        mobilityConstraints: [],
        preferredDailyPace: "moderate",
        earlyMorningStart: true,
        lateNightTolerance: false,
      },
      transport: {
        preferredModes: ["walking", "train", "local_transit", "bus"],
        avoidModes: ["private_transfer"],
        maxTransitHoursPerDay: 6,
        drivingComfort: "highway",
      },
      comfort: {
        budgetLevel: "moderate",
        hotelClass: 3,
        bedTypeRequirements: [],
        foodPreferences: ["local_cuisine", "traditional_restaurants", "home_cooking"],
        dietaryRestrictions: [],
        mealBudgetPerPerson: 35,
      },
      interests: {
        categories: ["history", "art", "architecture", "museums", "temples", "local_traditions", "folk_music", "workshops"],
        avoid: ["beach_resorts", "theme_parks", "shopping_malls"],
      },
      safety: {
        riskTolerance: "moderate",
        avoidNightTravel: false,
        languageComfort: [],
        specialNeeds: [],
        travelDocuments: [],
      },
    },
  },
];

export function getArchetypeById(id: string): ArchetypeDefinition | undefined {
  return TRAVELER_ARCHETYPES.find((a) => a.id === id);
}

export function getArchetypeIcon(iconName: string) {
  return iconName;
}
